import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Users, UserPlus, Power, KeyRound, Trash2, ShieldCheck, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../../../services/api';

const API = `${API_BASE_URL}/api/admin/users`;

const ROLE_BADGE = {
  fondateur: 'bg-primary/15 text-primary',
  'co-fondateur': 'bg-secondary/20 text-secondary-foreground',
  membre: 'bg-emerald-500/15 text-emerald-600',
  invite: 'bg-sky-500/15 text-sky-600',
};

const UsersAdmin = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, auth);
      setUsers(res.data?.users || []);
    } catch (e) {
      console.error('Erreur chargement utilisateurs', e);
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur de chargement' });
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const createVisitor = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMsg(null);
    try {
      const res = await axios.post(API, form, auth);
      setMsg({ type: 'success', text: `Compte visiteur créé : ${res.data.email}` });
      setForm({ email: '', password: '', first_name: '', last_name: '' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur lors de la création' });
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (u) => {
    try {
      await axios.patch(`${API}/${u.id}/status`, { is_active: !u.is_active }, auth);
      await load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Nouveau mot de passe pour ${u.email} (min. 8 caractères) :`);
    if (!pw) return;
    try {
      await axios.post(`${API}/${u.id}/reset-password`, { password: pw }, auth);
      setMsg({ type: 'success', text: `Mot de passe mis à jour pour ${u.email}` });
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte ${u.email} ?`)) return;
    try {
      await axios.delete(`${API}/${u.id}`, auth);
      await load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erreur' }); }
  };

  const isProtected = (u) => u.user_type === 'fondateur' || u.user_type === 'co-fondateur';

  return (
    <div className="min-h-screen" data-testid="users-admin-page">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Utilisateurs</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Créez et gérez des comptes <strong>visiteurs</strong> (rôle membre, aucun accès administrateur).
          Ils se connectent ensuite via la page « Se connecter » avec leur email et mot de passe.
        </p>

        {msg && (
          <div className={`mb-5 rounded-lg px-4 py-3 text-sm ${msg.type === 'success' ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/30' : 'bg-destructive/10 text-destructive border border-destructive/30'}`} data-testid="users-admin-message">
            {msg.text}
          </div>
        )}

        {/* Formulaire de création */}
        <Card className="mb-8" data-testid="create-visitor-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Créer un compte visiteur</h2>
            </div>
            <form onSubmit={createVisitor} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="visiteur@exemple.com" className="mt-1" data-testid="visitor-email-input" />
              </div>
              <div>
                <Label htmlFor="password">Mot de passe (min. 8)</Label>
                <Input id="password" type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="mt-1" data-testid="visitor-password-input" />
              </div>
              <div>
                <Label htmlFor="first_name">Prénom</Label>
                <Input id="first_name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Visiteur" className="mt-1" data-testid="visitor-firstname-input" />
              </div>
              <div>
                <Label htmlFor="last_name">Nom</Label>
                <Input id="last_name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="PADAID" className="mt-1" data-testid="visitor-lastname-input" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={creating} className="flex items-center gap-2" data-testid="create-visitor-submit">
                  <UserPlus className="h-4 w-4" /> {creating ? 'Création…' : 'Créer le visiteur'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Liste des utilisateurs */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Comptes existants</h2>
          <Button variant="outline" size="sm" onClick={load} className="flex items-center gap-2" data-testid="refresh-users-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : (
          <div className="space-y-3" data-testid="users-list">
            {users.map((u) => (
              <Card key={u.id} data-testid={`user-row-${u.email}`}>
                <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{u.email}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGE[u.user_type] || 'bg-muted text-muted-foreground'}`}>
                        {isProtected(u) && <ShieldCheck className="h-3 w-3" />}{u.user_type}
                      </span>
                      {!u.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">désactivé</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{[u.first_name, u.last_name].filter(Boolean).join(' ')}</p>
                  </div>
                  {!isProtected(u) && (
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => toggleStatus(u)} title={u.is_active ? 'Désactiver' : 'Activer'} data-testid={`toggle-status-${u.email}`}>
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => resetPassword(u)} title="Réinitialiser le mot de passe" data-testid={`reset-pw-${u.email}`}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => removeUser(u)} title="Supprimer" className="text-destructive hover:bg-destructive/10" data-testid={`delete-user-${u.email}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersAdmin;
